import React, { useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { List, ListItem, Typography } from '@mui/material'

import { Icon } from '../Icon'

import { truncatePagination } from '~/utils/pagination'

type PaginationProps = {
  pageType: 'blog' | 'customers'
  searchText?: string
  activePage: number
  updatePage?: (page: number) => void
  totalPages: number
  category?: string
}

export const Pagination = ({
  pageType,
  searchText,
  activePage,
  updatePage,
  totalPages,
  category
}: PaginationProps) => {
  const [truncatedPagination, setTruncatedPagination] = useState<
    (number | '...')[]
  >([])

  useEffect(() => {
    const paginated = truncatePagination(totalPages, activePage, 1)
    setTruncatedPagination(paginated || [])
  }, [totalPages, activePage])

  const generateUrl = (type: string, page?: number) => {
    let url = `/${pageType}`
    if (category && category !== 'All') url += `/${category.toLowerCase()}`

    switch (type) {
      case 'previous':
        url += activePage > 2 ? `/${activePage - 1}` : ''
        break
      case 'next':
        url += `/${page && page <= totalPages ? activePage + 1 : activePage}`
        break
      default:
        url += page && page > 1 ? `/${page}` : ''
    }

    return url
  }

  const handlePageChange = (type: string, page?: number) => () => {
    if (searchText) {
      if (typeof updatePage !== 'undefined') updatePage(page || 0)
    } else {
      navigate(generateUrl(type, page))
    }
  }

  if (totalPages <= 1) return null

  return (
    <List
      data-test='pagination'
      disablePadding
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: { xs: 5, mobile: 7, laptop: 10 }
      }}
    >
      <ListItem data-test='prev-page'
        disabled={activePage === 1}
        sx={{
          width: 'fit-content',
          cursor: activePage === 1 ? 'default' : 'pointer',
          mr: 2,
          p: 0
        }}
        onClick={handlePageChange('previous', activePage - 1)}
      >
        <Icon icon='chevron-left' color='text.primary' />
      </ListItem>

      {truncatedPagination.map(page => (
        <ListItem
          key={page}
          data-test={'list-page'}
          disablePadding
          sx={{
            width: 'fit-content',
            justifyContent: 'center',
            mx: 0.5,
            px: 1,
            ...(activePage === page && {
              '&::after': {
                width: '100%',
                height: 2,
                bgcolor: 'text.primary',
                borderRadius: 2,
                bottom: -10,
                content: '""',
                position: 'absolute'
              }
            })
          }}
        >
          {page === '...' ? (
            <Typography sx={{ fontWeight: 'bold' }}>{page}</Typography>
          ) : (
            <Typography
              onClick={handlePageChange('page', page)}
              sx={{
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {page}
            </Typography>
          )}
        </ListItem>
      ))}

      <ListItem
        data-test='next-page'
        disabled={activePage === totalPages}
        sx={{
          width: 'fit-content',
          cursor: activePage === totalPages ? 'default' : 'pointer',
          ml: 2,
          p: 0
        }}
        onClick={ handlePageChange('next', activePage + 1)}
      >
        <Icon icon='chevron-right' color='text.primary' />
      </ListItem>
    </List>
  )
}
